import { getPickedEmployee } from '@/helpers/employee';
import { serviceIsExtraTimeAddOn } from '@/helpers/service';
import { useAppSelector } from '@/hooks';
import { BookState } from '@/types/state/book';
import { ANY_EMPLOYEE_ID, getAnyEmployee } from '../constants';
import { getBookableEmployeeListItems, toEmployeeListItem } from '../views/SelectEmployeeList.helpers';
import type { SelectEmployeeListItem } from '../views/SelectEmployeeList.types';
import type { SelectionDrawerEmployee } from './SelectionDrawer.types';

export function useSelectionDrawerEmployee(): {
  employee: SelectionDrawerEmployee | undefined;
  employees: SelectEmployeeListItem[];
  selectedEmployeeId: number | undefined;
  getSrToken: (employeeId: number) => string | undefined;
} {
  const { employee: pickedEmployeeId, place, services } = useAppSelector((state) => state.book) as BookState;
  const pickedServices = services ?? [];

  // Extra-time add-ons are excluded: they live on `service.extra.addOnServices`, never in
  // `employee.services`, so one in the selection would filter out every employee.
  const employeeServiceIds = Array.from(
    new Set(pickedServices.filter((service) => !serviceIsExtraTimeAddOn(service)).map((service) => service.id)),
  );
  const { bookableEmployees, items } = place
    ? getBookableEmployeeListItems(place, employeeServiceIds)
    : { bookableEmployees: [], items: [] };

  const anyEmployee = getAnyEmployee();
  // A deep link can pick an employee the AND-filtered list excludes — fall back to their
  // place record, since this row is the only route into the employee view.
  const pickedListItem = items.find((item) => item.id === pickedEmployeeId);
  const pickedPlaceRecord =
    !pickedListItem && pickedEmployeeId != null && pickedEmployeeId !== ANY_EMPLOYEE_ID
      ? getPickedEmployee(pickedEmployeeId, place?.employees ?? [])
      : undefined;
  const employee: SelectionDrawerEmployee | undefined = place?.about?.settings?.hideEmployees
    ? undefined
    : pickedEmployeeId === ANY_EMPLOYEE_ID
    ? { name: anyEmployee.about.name, jobTitle: anyEmployee.about.jobTitle, avatarIcon: 'users-alt' }
    : pickedListItem ?? (pickedPlaceRecord && place ? toEmployeeListItem(pickedPlaceRecord, place) : undefined);

  const getSrToken = (employeeId: number) =>
    employeeId === ANY_EMPLOYEE_ID
      ? String(ANY_EMPLOYEE_ID)
      : bookableEmployees.find((candidate) => candidate.id === employeeId)?.about?.externalId;

  return { employee, employees: items, selectedEmployeeId: pickedEmployeeId ?? undefined, getSrToken };
}
